import { useDevice } from '@opendoor/bricks/hooks/useMedia';
import { Layout, LayoutContent } from 'platform/components/layout/Layout';
import { InnerSellerHeader } from 'sellDirect/components/offer/SellerHeader';
import React from 'react';
import { Box, Flex, Image, Text } from '@opendoor/bricks/core';
import { OdProtosWebData_RecordedFeature as RecordedFeature } from '__generated__/globalTypes';
import { VideoInteraction } from 'sellDirect/components/ssva/VideoInteraction';
import { FileInteraction } from 'sellDirect/components/ssva/FileInteraction';
import { useQualificationContext } from 'shared/helpers/QualificationContext';
import { generatePath } from 'react-router-dom';
import { Redirect } from 'platform/react-router';
import { getHubOverviewRoute } from 'shared/helpers/routes';
import { PreClosingOffer } from 'sellDirect/OfferContext';

type TaskUploadPageProps = {
  recordedFeature: RecordedFeature;
  offer: PreClosingOffer;
};

type VideoInstructions = {
  title: string;
  body: Array<string>;
};

const INTERIOR_INSTRUCTIONS: VideoInstructions = {
  title: 'Interior video',
  body: [
    'Make sure each room is well-lit',
    'Capture the floor and ceiling',
    'Move the camera slowly across each room',
    'Each video should be no more than 10 minutes long',
  ],
};

const BACKYARD_INSTRUCTIONS: VideoInstructions = {
  title: 'Backyard',
  body: [
    'Start at the backyard and face the back of your house',
    'Move the camera around slowly to get a 360-degree view of the backyard',
  ],
};

const FRONTYARD_INSTRUCTIONS: VideoInstructions = {
  title: 'Front yard',
  body: [
    'Stand at the curb facing the front of your house',
    'Move the camera slowly from one side of your house to the other',
    'Move to the front door of the house and stop the video',
  ],
};

const WHOLE_HOME_INSTRUCTIONS: VideoInstructions = {
  title: 'Share a video of the home',
  body: [
    'Make sure each room is well-lit',
    'Capture the floor and ceiling',
    'Move the camera slowly across each room',
    'Be sure to include the front and backyard',
    'If you submit a link, make sure all videos are in one folder and permissions are viewable by all (YouTube, Matterport, Google Drive, Dropbox, iCloud, OneDrive, Samsung Cloud, Loom, InsideMaps, Vimeo, MyCloud are accepted)',
  ],
};

const SELLER_DISCLOSURE_INSTRUCTIONS: VideoInstructions = {
  title: "Upload Seller's disclosure",
  body: [
    'Since the home is already listed, upload the seller’s disclosure to get a final offer within days.',
  ],
};

const INSTRUCTION_MAP: Record<string, VideoInstructions> = {
  INTERIOR: INTERIOR_INSTRUCTIONS,
  BACKYARD: BACKYARD_INSTRUCTIONS,
  FRONTYARD: FRONTYARD_INSTRUCTIONS,
  WHOLE_HOME: WHOLE_HOME_INSTRUCTIONS,
  SELLER_DISCLOSURE_DOCUMENT: SELLER_DISCLOSURE_INSTRUCTIONS,
};

/**
 * Returns the video instructions based on recordedFeature
 */
export const Instructions: React.FC<TaskUploadPageProps> = ({ recordedFeature, offer }) => {
  const { isSellDirectQualified, addressUUID } = useQualificationContext();
  const instructionBody = INSTRUCTION_MAP[recordedFeature]?.body;

  if (!isSellDirectQualified || offer?.isExpired) {
    /*
    Redirect to the overview page if this user is loading on /upload
    but it's not sellDirect qualified, or the offer has already expired
    , or the customer is an agent customer
    */
    return <Redirect to={generatePath(getHubOverviewRoute(), { addressUUID })} />;
  }

  return (
    <Box mt={[null, null, '7']} mb={6} width={['327px', null, '378px', '500px']}>
      {instructionBody.map((item, id) => (
        <Flex key={`tip-${id}`} alignItems="start" mb={2}>
          {instructionBody.length > 1 && (
            <Image
              minWidth="20px"
              height="20px"
              src="https://imgdrop.imgix.net/f89affd4777340ea9660dc8ae8f5afed.svg"
              alt="Checkmark icon"
              mr={3}
              mt={2}
            />
          )}
          <Box>
            <Text size="s1">{item}</Text>
          </Box>
        </Flex>
      ))}
    </Box>
  );
};

function submittedInfoByFeature(
  recordedFeature: RecordedFeature,
  ssvaInteriorRecordingsSubmitted: boolean,
  ssvaFrontyardRecordingsSubmitted: boolean,
  ssvaBackyardRecordingsSubmitted: boolean,
  ssvaWholeHomeRecordingsSubmitted: boolean,
) {
  switch (recordedFeature) {
    case RecordedFeature.INTERIOR: {
      return ssvaInteriorRecordingsSubmitted;
    }
    case RecordedFeature.FRONTYARD: {
      return ssvaFrontyardRecordingsSubmitted;
    }
    case RecordedFeature.BACKYARD: {
      return ssvaBackyardRecordingsSubmitted;
    }
    case RecordedFeature.WHOLE_HOME: {
      return ssvaWholeHomeRecordingsSubmitted;
    }
    default: {
      return false;
    }
  }
}

/**
 * Displays the video uplaod Page
 * @param props TaskUploadPageProps
 * @returns TaskUploadPage
 */
export const TaskUploadPage: React.FC<TaskUploadPageProps> = ({ recordedFeature, offer }) => {
  const { isMobile } = useDevice();
  const {
    ssvaData: {
      ssvaData: {
        ssvaRecordings,
        ssvaInteriorRecordingsSubmitted,
        ssvaFrontyardRecordingsSubmitted,
        ssvaBackyardRecordingsSubmitted,
        ssvaSellerDisclosureDocumentRecordingsSubmitted,
        ssvaWholeHomeRecordingsSubmitted,
      },
    },
  } = offer;

  return (
    <Layout
      data-testid="hub-virtual-assessment-upload-page"
      hideNav
      renderHeader={() => <InnerSellerHeader title={INSTRUCTION_MAP?.[recordedFeature]?.title} />}
    >
      <LayoutContent p="0" maxWidth="100%">
        <Flex
          backgroundColor={['neutrals0', null, 'neutrals10']}
          borderRadius="roundedSmooth"
          p="9"
          my="10"
          width={['100%', null, '70%']}
          justifyContent="center"
        >
          <Flex
            maxWidth={['327px', null, '579px']}
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
          >
            <Flex mb="7" flexDirection="column">
              {!isMobile && (
                <Text as="h2" fontSize="s4" fontWeight="bold">
                  {INSTRUCTION_MAP[recordedFeature]?.title}
                </Text>
              )}
              <Instructions recordedFeature={recordedFeature} offer={offer} />
            </Flex>

            {recordedFeature === RecordedFeature.SELLER_DISCLOSURE_DOCUMENT ? (
              <FileInteraction
                recordedFeature={recordedFeature}
                submissionCompleted={ssvaSellerDisclosureDocumentRecordingsSubmitted}
                ssvaRecordings={offer.ssvaData.ssvaData.ssvaRecordings}
              />
            ) : (
              <VideoInteraction
                uploadedFiles={
                  ssvaRecordings.filter(
                    (recording) => recording.recordedFeature === recordedFeature,
                  ) ?? []
                }
                recordedFeature={recordedFeature}
                submissionCompleted={submittedInfoByFeature(
                  recordedFeature,
                  ssvaInteriorRecordingsSubmitted,
                  ssvaFrontyardRecordingsSubmitted,
                  ssvaBackyardRecordingsSubmitted,
                  ssvaWholeHomeRecordingsSubmitted,
                )}
                ssvaState={offer.ssvaData.ssvaState}
              />
            )}
          </Flex>
        </Flex>
      </LayoutContent>
    </Layout>
  );
};
